import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import logo from "../../assets/svg/auth/logo.svg";
import { NAV_ICONS, NavIcon } from "./navIcons";
import { ROUTES } from "../../routes/paths";
import { useAdmin } from "../../features/auth";

interface SidebarItemProps {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  icon,
  label,
  active,
  onClick,
}) => (
  <button
    onClick={onClick}
    aria-current={active ? "page" : undefined}
    // Text and icon keep the same ink in both states — only the pill's fill
    // and hairline border mark the active item. The inactive border is
    // transparent rather than absent so nothing shifts by half a pixel.
    className={`w-full flex items-center gap-3 px-3 py-2.5 3xl:px-4 3xl:py-3 rounded-[6px] text-[14px] 3xl:text-[16px] text-[#00424B] border-[0.5px] transition-colors ${
      active
        ? "bg-[#EFFBFD] border-[#5BD3E1] font-medium"
        : "border-transparent hover:bg-[#EFFBFD]/60"
    }`}
  >
    <span className="w-[18px] h-[18px] 3xl:w-[20px] 3xl:h-[20px] flex-shrink-0">
      {icon}
    </span>
    <span className="truncate">{label}</span>
  </button>
);

interface NavItem {
  path: string;
  label: string;
  /** SVGR component or a lucide icon — both take a className to size them. */
  Icon: NavIcon;
}

const Separator = () => <div className="my-3 mx-1 border-t border-[#D7F9FD]" />;

interface SidebarProps {
  /** Mobile drawer state. Ignored from `md` up, where the rail is static. */
  open: boolean;
  onClose: () => void;
}

/**
 * The rail every signed-in screen sits beside: the mark, three destinations,
 * and who is signed in at the foot of it.
 *
 * The account block carries no action of its own — signing out is on the
 * Settings screen, with the rest of what can be done to an account.
 */
const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { admin, email } = useAdmin();

  // Both are known the moment there is a session — see `useAdmin` — so the rail
  // never draws a blank waiting for a name. The address is the fallback for a
  // session stored before the name was kept.
  const displayName = admin?.name || email || "Admin";

  // Everybody who can sign in here is an admin, and the API has no role to
  // send, so this is a label rather than a fact read off the account.
  const roleLine = "Admin";

  // Active item is derived from the URL, so deep links and browser history
  // highlight correctly without any extra state to keep in sync.
  const isActive = (path: string) => location.pathname.startsWith(path);

  const iconClass = "w-full h-full";

  const items: (NavItem | "separator")[] = [
    {
      path: ROUTES.dashboard,
      label: "Dashboard",
      Icon: NAV_ICONS[ROUTES.dashboard],
    },
    { path: ROUTES.users, label: "Users", Icon: NAV_ICONS[ROUTES.users] },
    {
      path: ROUTES.revenue,
      label: "Revenue",
      Icon: NAV_ICONS[ROUTES.revenue],
    },
    "separator",
    {
      path: ROUTES.settings,
      label: "Settings",
      Icon: NAV_ICONS[ROUTES.settings],
    },
  ];

  return (
    <>
      {/* Backdrop — only ever visible while the mobile drawer is open. */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={`bg-[#F8FEFF] border-r-[0.5px] border-[#A6EDF5] flex flex-col overflow-hidden
          fixed inset-y-0 left-0 z-40 w-[240px] transition-transform duration-300
          ${open ? "translate-x-0" : "-translate-x-full"}
          md:static md:translate-x-0 md:z-auto md:flex-shrink-0 md:h-full
          md:w-[180px] lg:w-[192px] 2xl:w-[210px] 3xl:w-[240px] 4xl:w-[280px]`}
      >
        <div className="flex-shrink-0 flex items-center justify-between px-4 md:px-5 3xl:px-6 pt-6 pb-2">
          <img src={logo} alt="Naurist" className="h-6 3xl:h-7 w-auto" />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            className="md:hidden p-1 rounded-md text-[#00424B] hover:bg-[#EFFBFD]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto sidebar-nav-scroll px-3 3xl:px-4 mt-8 pb-2">
          <div className="flex flex-col gap-1">
            {items.map((item, index) =>
              item === "separator" ? (
                <Separator key={`sep-${index}`} />
              ) : (
                <SidebarItem
                  key={item.path}
                  icon={<item.Icon className={iconClass} />}
                  label={item.label}
                  active={isActive(item.path)}
                  onClick={() => navigate(item.path)}
                />
              )
            )}
          </div>
        </nav>

        {/* Who is signed in, pinned to the bottom. */}
        <div className="flex-shrink-0 px-4 3xl:px-5 py-4">
          <div className="flex items-center gap-3">
            {/* An initial, and only an initial: there is nowhere to upload a
                picture from and no route that would serve one back. */}
            <div className="w-9 h-9 3xl:w-10 3xl:h-10 rounded-full bg-[#EDE7F6] flex items-center justify-center flex-shrink-0 overflow-hidden">
              <span className="text-sm 3xl:text-base font-semibold text-[#5B3FA8]">
                {displayName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] 3xl:text-[15px] font-medium text-ink truncate">
                {displayName}
              </div>
              {/* What they are, rather than what they can do — the role is a
                  fact about the account, and the name above is what the eye
                  should land on, so it is set quiet. */}
              <div className="text-[11px] 3xl:text-[13px] text-[#8B8D98] truncate">
                {roleLine}
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
